// -- [Main marketing page - scroll-down SPA]

import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { ContentConfig, RoleKey } from '@/types/content'
import { getCurrentCopy } from '@/lib/content'
import { getStoredRole, getStoredTopic, setStoredRole, setStoredTopic } from '@/lib/storage'
import { parseRoleTopicFromUrl, buildRoleTopicHash } from '@/lib/url'
import { shouldOpenSignupOnLoad, getSubmittedDemoEmail } from '@/lib/signupDraft'
import { Hero } from './Hero'
import { StickySelector } from './StickySelector'
import { VideoSection } from './VideoSection'
import { OutcomesSection } from './OutcomesSection'
import { RoleSection } from './RoleSection'
import { StorageCalculatorSection } from './StorageCalculatorSection'
import { FeatureGrid } from './FeatureGrid'
import { EmbedSection } from './EmbedSection'
import { DealershipLogos } from './DealershipLogos'
import { StatsBand } from './StatsBand'
import { FaqSection } from './FaqSection'
import { PricingSection } from './PricingSection'
import { RoleHeadlinesSection } from './RoleHeadlinesSection'
import { CtaSection } from './CtaSection'
import { DemoSignupModal } from './DemoSignupModal'
import { Footer } from './Footer'

function getInitialRoleTopic(config: ContentConfig): { roleKey: RoleKey; topicKey: string } {
  const fromUrl = parseRoleTopicFromUrl(config)
  if (fromUrl) return fromUrl
  const storedRole = getStoredRole()
  // -- [Default: Dealer Principal / management + saving up to $80K]
  const roleKey: RoleKey =
    storedRole && config.roles[storedRole] ? storedRole : 'management'
  const topics = config.roles[roleKey]?.topics ?? []
  const storedTopic = getStoredTopic()
  const defaultTopicKey =
    topics.find((t) => t.key === 'save-80k')?.key ?? topics[0]?.key ?? ''
  const topicKey =
    storedTopic && topics.some((t) => t.key === storedTopic)
      ? storedTopic
      : defaultTopicKey
  return { roleKey, topicKey }
}

interface MarketingPageProps {
  config: ContentConfig
}

export function MarketingPage({ config }: MarketingPageProps) {
  const [roleKey, setRoleKeyState] = useState<RoleKey>(() =>
    getInitialRoleTopic(config).roleKey
  )
  const [topicKey, setTopicKeyState] = useState<string>(() =>
    getInitialRoleTopic(config).topicKey
  )
  const [stickyVisible, setStickyVisible] = useState(false)
  const [signupOpen, setSignupOpen] = useState(false)
  const [signupClickedButtons, setSignupClickedButtons] = useState<string[]>([])
  const [hasSubmittedDemo, setHasSubmittedDemo] = useState(() => !!getSubmittedDemoEmail())
  const heroRef = useRef<HTMLElement | null>(null)
  const isFromPopstate = useRef(false)

  // -- [Open signup modal on load when ?showFormOnPageLoad=true]
  useEffect(() => {
    if (shouldOpenSignupOnLoad()) setSignupOpen(true)
  }, [])

  // -- [Sync URL when role/topic changes; skip when change came from popstate]
  useEffect(() => {
    if (isFromPopstate.current) {
      isFromPopstate.current = false
      return
    }
    const hash = buildRoleTopicHash(roleKey, topicKey)
    if (window.location.hash === hash) return
    const url = window.location.pathname + window.location.search + hash
    const hadNoHash = !window.location.hash
    if (hadNoHash) {
      window.history.replaceState(null, '', url)
    } else {
      window.history.pushState(null, '', url)
    }
  }, [roleKey, topicKey])

  // -- [Handle back/forward]
  useEffect(() => {
    const onPopstate = () => {
      const parsed = parseRoleTopicFromUrl(config)
      if (parsed) {
        isFromPopstate.current = true
        setRoleKeyState(parsed.roleKey)
        setTopicKeyState(parsed.topicKey)
      }
    }
    window.addEventListener('popstate', onPopstate)
    return () => window.removeEventListener('popstate', onPopstate)
  }, [config])

  useEffect(() => {
    setStoredRole(roleKey)
    const topics = config.roles[roleKey]?.topics ?? []
    const validTopic = topics.some((t) => t.key === topicKey)
      ? topicKey
      : topics[0]?.key ?? ''
    setTopicKeyState(validTopic)
    setStoredTopic(validTopic)
  }, [roleKey])

  useEffect(() => {
    setStoredTopic(topicKey)
  }, [topicKey])

  const copy = useMemo(
    () => getCurrentCopy(config, roleKey, topicKey),
    [config, roleKey, topicKey]
  )

  // -- [Show sticky bar only when hero has left the viewport so the bar is "attached to the top"]
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: '0px' }
    )
    const el = document.getElementById('hero')
    if (el) {
      heroRef.current = el
      observer.observe(el)
    }
    return () => observer.disconnect()
  }, [])

  // -- [Umami: section viewed ≥2s and scroll-to-bottom]
  const SECTION_IDS = ['features', 'customers', 'qa', 'pricing', 'contact']
  useEffect(() => {
    const viewedSections = new Set<string>()
    const sectionTimeouts: Record<string, ReturnType<typeof setTimeout>> = {}

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id
          if (!id || !SECTION_IDS.includes(id)) continue
          if (entry.isIntersecting) {
            if (viewedSections.has(id)) continue
            sectionTimeouts[id] = setTimeout(() => {
              viewedSections.add(id)
              delete sectionTimeouts[id]
              window.umami?.track('Section view', { section: id })
            }, 2000)
          } else {
            if (sectionTimeouts[id]) {
              clearTimeout(sectionTimeouts[id])
              delete sectionTimeouts[id]
            }
          }
        }
      },
      { threshold: 0.25, rootMargin: '0px' }
    )

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id)
      if (el) sectionObserver.observe(el)
    })

    let bottomTracked = false
    const checkBottom = () => {
      if (bottomTracked) return
      const scrollBottom = window.scrollY + window.innerHeight
      const docHeight = document.documentElement.scrollHeight - 80
      if (scrollBottom >= docHeight) {
        bottomTracked = true
        window.umami?.track('Scroll to bottom')
      }
    }
    window.addEventListener('scroll', checkBottom, { passive: true })
    checkBottom()

    return () => {
      sectionObserver.disconnect()
      Object.values(sectionTimeouts).forEach(clearTimeout)
      window.removeEventListener('scroll', checkBottom)
    }
  }, [])

  const setRoleKey = (r: RoleKey) => {
    setRoleKeyState(r)
    const topics = config.roles[r]?.topics ?? []
    setTopicKeyState(topics[0]?.key ?? '')
    window.umami?.track('Role change', { role: config.roles[r]?.label ?? r })
  }

  const setTopicKey = (topic: string) => {
    setTopicKeyState(topic)
    const topicLabel = config.roles[roleKey]?.topics.find((t) => t.key === topic)?.label ?? topic
    window.umami?.track('Topic change', { topic: topicLabel })
  }

  const openSignup = useCallback((source: string) => {
    setSignupClickedButtons((prev) => [...prev, source])
    setSignupOpen(true)
  }, [])

  const handleSignupSuccess = useCallback(() => {
    setSignupClickedButtons([])
    setHasSubmittedDemo(true)
  }, [])

  return (
    <div>
      <DemoSignupModal
        open={signupOpen}
        onOpenChange={setSignupOpen}
        clickedButtons={signupClickedButtons}
        roleKey={roleKey}
        topicKey={topicKey}
        onSuccess={handleSignupSuccess}
      />
      <main>
      <Hero
        config={config}
        copy={copy}
        roleKey={roleKey}
        topicKey={topicKey}
        onRoleChange={setRoleKey}
        onTopicChange={setTopicKey}
        onOpenSignup={openSignup}
        hasSubmittedDemo={hasSubmittedDemo}
      />
      <StickySelector
        config={config}
        roleKey={roleKey}
        topicKey={topicKey}
        onRoleChange={setRoleKey}
        onTopicChange={setTopicKey}
        onOpenSignup={openSignup}
        visible={stickyVisible}
        hasSubmittedDemo={hasSubmittedDemo}
      />
      <VideoSection
        video={config.roles[roleKey]?.topics.find((t) => t.key === topicKey)?.video}
      />
      <OutcomesSection
        cards={config.outcomeCards}
        roleKey={roleKey}
        emphasisedSections={copy.emphasisedSections}
      />
      {/* -- [Only show the role section matching the selected role] */}
      {(() => {
        const section = config.sections[roleKey]
        if (!section) return null
        return <RoleSection key={roleKey} section={section} />
      })()}
      <StorageCalculatorSection roleKey={roleKey} />
      <EmbedSection
        embeds={config.roles[roleKey]?.topics.find((t) => t.key === topicKey)?.embeds ?? []}
      />
      <FeatureGrid tiles={config.featureTiles} roleKey={roleKey} />
      <EmbedSection embeds={config.embeds ?? []} />
      <DealershipLogos dealerships={config.dealerships} />
      <StatsBand roleKey={roleKey} />
      <FaqSection faq={config.faq} />
      {config.pricing && (
        <PricingSection pricing={config.pricing} onOpenSignup={openSignup} hasSubmittedDemo={hasSubmittedDemo} />
      )}
      <RoleHeadlinesSection roleKey={roleKey} />
      <CtaSection cta={config.cta} onOpenSignup={openSignup} hasSubmittedDemo={hasSubmittedDemo} />
      </main>
      <Footer footer={config.footer} />
    </div>
  )
}
